import * as CarTierSystem from './CarTierSystem.js';

const SAVE_KEY_LEGACY = 'roadtripGameState';
const SAVE_KEY_ANONYMOUS = 'roadTripSave_anonymous';
const SAVE_KEY_ACCOUNT = 'roadTripSave_account';

/**
 * Save format version for migration branching.
 * Increment when save structure changes to enable targeted migrations.
 *
 * Version history:
 * - 0: Legacy saves (pre-versioning)
 * - 1: First versioned format (2026-01-15)
 */
const SAVE_FORMAT_VERSION = 1;

/**
 * SaveManager handles all persistence logic for GameState
 * - Load/save to localStorage
 * - All save migrations for backwards compatibility
 * - Throttled saves for performance
 */
export class SaveManager {
    constructor(carsConfig, upgradesConfig) {
        this.carsConfig = carsConfig;
        this.upgradesConfig = upgradesConfig;
        this.lastSaveTime = 0;
        this.pendingSave = false;
        this.cloudSaveCallback = null; // For cloud sync integration
        this._activeSaveKey = SAVE_KEY_ANONYMOUS; // Default to anonymous slot
    }

    /**
     * Register a callback for cloud save synchronization
     * @param {Function} callback - Called with (state, force) when save occurs
     */
    registerCloudSaveCallback(callback) {
        this.cloudSaveCallback = callback;
    }

    /**
     * Load game state from localStorage
     * @returns {object|null} Parsed and migrated game state, or null if no save exists
     */
    load() {
        try {
            const saved = localStorage.getItem(this._activeSaveKey);
            if (!saved) return null;

            const parsed = JSON.parse(saved);
            return this._runMigrations(parsed);
        } catch (error) {
            console.error('Error loading game state:', error);
            return null;
        }
    }

    /**
     * Save game state to localStorage
     * @param {object} state - The game state to save
     * @param {boolean} force - If true, bypasses throttling
     */
    save(state, force = false) {
        const currentTime = performance.now();

        // Throttle saves to max once per second (unless forced)
        if (!force && currentTime - this.lastSaveTime < 1000) {
            this.pendingSave = true;
            return;
        }

        try {
            // Stamp the state with save time for conflict resolution
            state._lastSaveTime = Date.now();
            // Stamp with format version for migration branching
            state._saveFormatVersion = SAVE_FORMAT_VERSION;

            localStorage.setItem(this._activeSaveKey, JSON.stringify(state));
            this.lastSaveTime = currentTime;
            this.pendingSave = false;

            // Notify cloud save manager (if registered)
            if (this.cloudSaveCallback) {
                this.cloudSaveCallback(state, force);
            }
        } catch (error) {
            console.error('Error saving game state:', error);
        }
    }

    /**
     * Force save if there's a pending save
     * @param {object} state - The game state to save
     */
    flushPendingSave(state) {
        if (this.pendingSave) {
            this.save(state, true);
        }
    }

    // ===== Save Slot Methods =====

    /**
     * Get the currently active save key
     * @returns {string} The active save key
     */
    getActiveSaveKey() {
        return this._activeSaveKey;
    }

    /**
     * Switch to anonymous save slot
     */
    switchToAnonymous() {
        console.log('[SaveManager] Switching to ANONYMOUS slot');
        this._activeSaveKey = SAVE_KEY_ANONYMOUS;
    }

    /**
     * Switch to account save slot
     */
    switchToAccount() {
        console.log('[SaveManager] Switching to ACCOUNT slot');
        this._activeSaveKey = SAVE_KEY_ACCOUNT;
    }

    /**
     * Check if anonymous save slot has data
     * @returns {boolean}
     */
    hasAnonymousSave() {
        return localStorage.getItem(SAVE_KEY_ANONYMOUS) !== null;
    }

    /**
     * Check if account save slot has data
     * @returns {boolean}
     */
    hasAccountSave() {
        return localStorage.getItem(SAVE_KEY_ACCOUNT) !== null;
    }

    /**
     * Check if legacy save exists
     * @returns {boolean}
     */
    hasLegacySave() {
        return localStorage.getItem(SAVE_KEY_LEGACY) !== null;
    }

    /**
     * Get legacy save data
     * @returns {object|null} Parsed legacy save or null
     */
    getLegacySave() {
        try {
            const saved = localStorage.getItem(SAVE_KEY_LEGACY);
            if (!saved) return null;
            return JSON.parse(saved);
        } catch (error) {
            console.error('Error reading legacy save:', error);
            return null;
        }
    }

    /**
     * Clear the legacy save key
     */
    clearLegacySave() {
        localStorage.removeItem(SAVE_KEY_LEGACY);
    }

    /**
     * Promote anonymous save to account slot (copy data, then clear anonymous)
     */
    promoteAnonymousToAccount() {
        const anonymousData = localStorage.getItem(SAVE_KEY_ANONYMOUS);
        if (anonymousData) {
            localStorage.setItem(SAVE_KEY_ACCOUNT, anonymousData);
            localStorage.removeItem(SAVE_KEY_ANONYMOUS);
        }
    }

    /**
     * Run all migrations on loaded state
     * @param {object} state - Raw parsed state from localStorage
     * @returns {object} Migrated state
     */
    _runMigrations(state) {
        // Determine save format version (legacy saves don't have this field)
        const savedVersion = state._saveFormatVersion ?? 0;
        console.log(`[SaveManager] Loading save format v${savedVersion} (current: v${SAVE_FORMAT_VERSION})`);

        // Future version-based migration branching can be added here:
        // if (savedVersion < 2) { this._migrateSomethingForV2(state); }

        this._migrateAddTotalMiles(state);
        this._migrateRemoveDeprecatedUpgrades(state);
        this._migrateAddInventory(state);
        this._migrateToObjectInventory(state);
        this._migrateAddItemLevels(state);
        this._migrateRenameVibes(state);
        this._migrateAddWavesStructure(state);
        this._migrateToDynamicSpawn(state);
        this._migrateToPacksArray(state);
        this._migrateCarCosts(state);
        this._recoverNegativeMiles(state);
        this._migrateAddMilesInvested(state);
        this._migrateAddPrestige(state);
        this._migratePrestigeFormat(state);
        this._migrateAddAudioSettings(state);
        this._migrateAddSession(state);
        this._migrateAddFuelTank(state);
        this._migrateAddMomentumRate(state);
        this._migrateAddLifetimeMiles(state);
        this._migrateAddLeaderboardPeriods(state);
        this._migrateAudioSettingsToVolume(state);
        this._migrateAddWavedust(state);
        this._migrateGrandfatherItems(state);
        this._migrateAddItemStars(state);
        this._migrateAddItemPrestige(state);
        this._migrateAddItemSlotType(state);
        this._migrateAddCollection(state);
        this._migrateEnsureBaseFields(state);
        return state;
    }

    // ===== Individual Migrations =====

    /**
     * Ensure base fields exist (for incomplete cloud saves)
     * This handles cases where cloud data was saved with incomplete state
     */
    _migrateEnsureBaseFields(state) {
        // Ensure currentCarId exists
        if (!state.currentCarId) {
            state.currentCarId = 'starter';
            console.log('[SaveManager] Migration: Added missing currentCarId');
        }

        // Ensure gameTime exists
        if (state.gameTime === undefined) {
            state.gameTime = 43200; // Noon
            console.log('[SaveManager] Migration: Added missing gameTime');
        }

        // Ensure cars object exists with at least the starter car
        if (!state.cars || Object.keys(state.cars).length === 0) {
            // Deep copy from config
            state.cars = JSON.parse(JSON.stringify(this.carsConfig));
            console.log('[SaveManager] Migration: Added missing cars from config');
        }

        // Ensure the current car exists in cars
        if (state.cars && !state.cars[state.currentCarId]) {
            if (this.carsConfig[state.currentCarId]) {
                state.cars[state.currentCarId] = JSON.parse(JSON.stringify(this.carsConfig[state.currentCarId]));
                console.log('[SaveManager] Migration: Added missing current car:', state.currentCarId);
            }
        }

        // Ensure waves has all required fields
        if (state.waves) {
            if (!state.waves.availablePacks) {
                state.waves.availablePacks = [];
                console.log('[SaveManager] Migration: Added missing waves.availablePacks');
            }
            if (!state.waves.futureWaves) {
                state.waves.futureWaves = [];
            }
            if (!state.waves.seenWaveMilestones) {
                state.waves.seenWaveMilestones = [];
            }
            if (state.waves.lastSpawnDistance === undefined) {
                state.waves.lastSpawnDistance = 0;
            }
            if (state.waves.luckAccumulator === undefined) {
                state.waves.luckAccumulator = 0;
            }
            if (state.waves.packsPurchased === undefined) {
                state.waves.packsPurchased = 0;
            }
            if (state.waves.tutorialCollectedOffline === undefined) {
                state.waves.tutorialCollectedOffline = false;
            }
            if (state.waves.tutorialShown === undefined) {
                state.waves.tutorialShown = false;
            }
        }
    }

    /**
     * Add totalMiles field if it doesn't exist
     */
    _migrateAddTotalMiles(state) {
        if (state.totalMiles === undefined) {
            state.totalMiles = state.miles || 0;
        }
    }

    /**
     * Remove deprecated maxSpeedTime/cruiseTime upgrades from cars
     */
    _migrateRemoveDeprecatedUpgrades(state) {
        if (state.cars) {
            Object.values(state.cars).forEach(car => {
                if (car.upgradeLevels) {
                    delete car.upgradeLevels.cruiseTime;
                    delete car.upgradeLevels.maxSpeedTime;
                }
                if (car.ascensionBonus) {
                    delete car.ascensionBonus.maxSpeedTime;
                }
            });
        }
    }

    /**
     * Add inventory structure if it doesn't exist
     */
    _migrateAddInventory(state) {
        if (!state.inventory) {
            state.inventory = {
                items: [],
                equippedSlots: {
                    dashboard: null,
                    keychain: null,
                    rearview: null
                }
            };
        }
    }

    /**
     * Reset inventory if it contains old string-based IDs instead of objects
     */
    _migrateToObjectInventory(state) {
        if (state.inventory && state.inventory.items && state.inventory.items.length > 0) {
            if (typeof state.inventory.items[0] === 'string') {
                console.warn('⚠️ Old ID-based inventory detected. Resetting inventory due to new procedural item system.');
                state.inventory = {
                    items: [],
                    equippedSlots: {
                        dashboard: null,
                        keychain: null,
                        rearview: null
                    }
                };
            }
        }
    }

    /**
     * Add level system to items that don't have it
     */
    _migrateAddItemLevels(state) {
        if (state.inventory && state.inventory.items) {
            state.inventory.items.forEach(item => {
                if (item.level === undefined) {
                    item.level = 1;
                    item.baseEffects = item.effects; // Store original as base
                }
            });
            // Also migrate equipped items
            Object.keys(state.inventory.equippedSlots).forEach(slot => {
                const item = state.inventory.equippedSlots[slot];
                if (item && item.level === undefined) {
                    item.level = 1;
                    item.baseEffects = item.effects;
                }
            });
        }
    }

    /**
     * Rename vibesPacks to waves
     */
    _migrateRenameVibes(state) {
        if (state.vibesPacks && !state.waves) {
            state.waves = state.vibesPacks;
            delete state.vibesPacks;
        }
    }

    /**
     * Add waves structure if it doesn't exist
     */
    _migrateAddWavesStructure(state) {
        if (!state.waves) {
            state.waves = {
                nextMilestone: 25,
                availablePacks: [],
                packsPurchased: 0,
                tutorialShown: false
            };
        }
    }

    /**
     * Add dynamic spawn system fields to waves
     */
    _migrateToDynamicSpawn(state) {
        if (state.waves && state.waves.nextWaveAtMile === undefined) {
            state.waves.nextWaveAtMile = state.waves.nextMilestone || 25;
            state.waves.nextWaveQuality = 0.2;
            state.waves.luckAccumulator = 0;
            state.waves.lastSpawnDistance = 100;
        }
    }

    /**
     * Convert single availablePack to availablePacks array
     */
    _migrateToPacksArray(state) {
        if (state.waves && state.waves.availablePack !== undefined) {
            if (!state.waves.availablePacks) {
                state.waves.availablePacks = [];
            }
            if (state.waves.availablePack !== null) {
                state.waves.availablePacks.push(state.waves.availablePack);
            }
            delete state.waves.availablePack;
        }
    }

    /**
     * Update car costs to match current config (fixes negative miles bug)
     */
    _migrateCarCosts(state) {
        if (state.cars) {
            Object.keys(state.cars).forEach(carId => {
                const savedCar = state.cars[carId];
                const configCar = this.carsConfig[carId];

                if (configCar && savedCar.unlockCost !== configCar.unlockCost) {
                    console.log(`Updating ${carId} cost: ${savedCar.unlockCost} -> ${configCar.unlockCost}`);
                    savedCar.unlockCost = configCar.unlockCost;
                }
            });
        }
    }

    /**
     * Fix negative miles caused by outdated car costs bug
     */
    _recoverNegativeMiles(state) {
        if (state.miles < 0) {
            console.warn(`Detected negative miles: ${state.miles}`);
            console.warn('Applying compensation for negative miles bug...');
            // Give them their absolute value back plus 50% compensation
            const compensation = Math.abs(state.miles) * 1.5;
            state.miles = compensation;
            console.log(`Restored miles to: ${state.miles}`);
        }
    }

    /**
     * Calculate and add milesInvested for tier system
     */
    _migrateAddMilesInvested(state) {
        if (state.cars) {
            Object.keys(state.cars).forEach(carId => {
                const car = state.cars[carId];
                if (car.milesInvested === undefined) {
                    car.milesInvested = CarTierSystem.calculateMilesInvestedFromUpgrades(
                        car,
                        carId,
                        this.upgradesConfig
                    );
                    console.log(`Migrated ${carId} milesInvested: ${car.milesInvested}`);
                }
            });
        }
    }

    /**
     * Add prestige state structure if it doesn't exist
     */
    _migrateAddPrestige(state) {
        if (!state.prestige) {
            state.prestige = {
                level: 0,
                mileEarningBonus: 0,
                skillBonuses: {
                    baseSpeed: 0,
                    acceleration: 0,
                    maxSpeed: 0,
                    cruiseDuration: 0,
                    cruiseCooldown: 0,
                    momentum: 0
                }
            };
        }
    }

    /**
     * Convert old idleSpeedBonus prestige format to skillBonuses format
     */
    _migratePrestigeFormat(state) {
        if (state.prestige && state.prestige.idleSpeedBonus !== undefined && !state.prestige.skillBonuses) {
            state.prestige.skillBonuses = {
                baseSpeed: state.prestige.idleSpeedBonus,
                acceleration: 0,
                maxSpeed: 0,
                cruiseDuration: 0,
                cruiseCooldown: 0,
                momentum: 0
            };
            delete state.prestige.idleSpeedBonus;
        }
    }

    /**
     * Add audioSettings if it doesn't exist
     */
    _migrateAddAudioSettings(state) {
        if (!state.audioSettings) {
            state.audioSettings = {
                sfxMuted: false,
                musicMuted: false
            };
        }
    }

    /**
     * Add session state for offline simulation if it doesn't exist
     */
    _migrateAddSession(state) {
        if (!state.session) {
            state.session = {
                exitTime: null,
                exitMomentum: 0,
                exitTotalMiles: 0,
                exitGameTime: 0
            };
        }
    }

    /**
     * Migrate fuel system: offlineFuel -> fuelTank, add fuel resource
     */
    _migrateAddFuelTank(state) {
        // Ensure driver object exists
        if (!state.driver) {
            state.driver = {};
        }

        // Migrate old top-level offlineFuel to driver.fuelTank
        if (state.offlineFuel && !state.driver.fuelTank) {
            state.driver.fuelTank = { level: state.offlineFuel.level || 0 };
            delete state.offlineFuel;
        }

        // Migrate driver.offlineFuel to driver.fuelTank
        if (state.driver.offlineFuel && !state.driver.fuelTank) {
            state.driver.fuelTank = { level: state.driver.offlineFuel.level || 0 };
            delete state.driver.offlineFuel;
        }

        // Ensure all driver skills exist
        if (!state.driver.fuelTank) {
            state.driver.fuelTank = { level: 0 };
        }
        if (!state.driver.scrapBonus) {
            state.driver.scrapBonus = { level: 0 };
        }
        if (!state.driver.itemUpgradeBonus) {
            state.driver.itemUpgradeBonus = { level: 0 };
        }
        if (!state.driver.waveLuck) {
            state.driver.waveLuck = { level: 0 };
        }

        // Initialize fuel to tank capacity if not set
        // This ensures existing players start with a full tank
        if (state.fuel === undefined) {
            const baseTime = 30 * 60; // 30 minutes
            const maxTime = 12 * 60 * 60; // 12 hours
            const level = state.driver.fuelTank?.level || 0;
            const capacity = baseTime + (maxTime - baseTime) * (level / 10);
            state.fuel = capacity;
        }
    }

    /**
     * Add momentumRate upgrade level to cars and prestige skill bonuses
     */
    _migrateAddMomentumRate(state) {
        // Add momentumRate to each car's upgradeLevels
        if (state.cars) {
            Object.values(state.cars).forEach(car => {
                if (car.upgradeLevels && car.upgradeLevels.momentumRate === undefined) {
                    car.upgradeLevels.momentumRate = 0;
                }
            });
        }

        // Add momentumRate to prestige skill bonuses
        if (state.prestige?.skillBonuses && state.prestige.skillBonuses.momentumRate === undefined) {
            state.prestige.skillBonuses.momentumRate = 0;
        }
    }

    /**
     * Add lifetimeMiles field for leaderboards (never resets, even on prestige)
     */
    _migrateAddLifetimeMiles(state) {
        if (state.lifetimeMiles === undefined) {
            // Initialize to totalMiles (best available data for existing players)
            state.lifetimeMiles = state.totalMiles || 0;
            console.log('[SaveManager] Migration: Added lifetimeMiles:', state.lifetimeMiles);
        }
    }

    /**
     * Add leaderboard period tracking for weekly/daily miles
     */
    _migrateAddLeaderboardPeriods(state) {
        if (!state.leaderboardPeriods) {
            // Initialize start miles to current totalMiles so existing players
            // start fresh rather than showing their entire history as "this week"
            state.leaderboardPeriods = {
                weekStartTime: null,
                weekStartMiles: state.totalMiles || 0,
                dayStartTime: null,
                dayStartMiles: state.totalMiles || 0
            };
            console.log('[SaveManager] Migration: Added leaderboardPeriods, starting from', state.totalMiles || 0);
        }

        // Fix for users who got the broken migration (weekStartMiles = 0 but have miles)
        // This ensures they don't show their entire history as weekly/daily
        if (state.leaderboardPeriods.weekStartMiles === 0 && (state.totalMiles || 0) > 1000) {
            state.leaderboardPeriods.weekStartMiles = state.totalMiles;
            state.leaderboardPeriods.weekStartTime = null; // Force re-check
            console.log('[SaveManager] Migration: Fixed weekStartMiles to', state.totalMiles);
        }
        if (state.leaderboardPeriods.dayStartMiles === 0 && (state.totalMiles || 0) > 1000) {
            state.leaderboardPeriods.dayStartMiles = state.totalMiles;
            state.leaderboardPeriods.dayStartTime = null; // Force re-check
            console.log('[SaveManager] Migration: Fixed dayStartMiles to', state.totalMiles);
        }
    }

    /**
     * Migrate audio settings from muted booleans to volume numbers
     */
    _migrateAudioSettingsToVolume(state) {
        if (!state.audioSettings) {
            state.audioSettings = { sfxVolume: 100, musicVolume: 50 };
            return;
        }

        // Convert old muted booleans to volume
        if (state.audioSettings.sfxMuted !== undefined) {
            state.audioSettings.sfxVolume = state.audioSettings.sfxMuted ? 0 : 100;
            delete state.audioSettings.sfxMuted;
            console.log('[SaveManager] Migration: Converted sfxMuted to sfxVolume');
        }

        if (state.audioSettings.musicMuted !== undefined) {
            state.audioSettings.musicVolume = state.audioSettings.musicMuted ? 0 : 50;
            delete state.audioSettings.musicMuted;
            console.log('[SaveManager] Migration: Converted musicMuted to musicVolume');
        }

        // Ensure volume fields exist
        if (state.audioSettings.sfxVolume === undefined) {
            state.audioSettings.sfxVolume = 100;
        }
        if (state.audioSettings.musicVolume === undefined) {
            state.audioSettings.musicVolume = 50;
        }
    }

    /**
     * Add wavedust currency field
     */
    _migrateAddWavedust(state) {
        if (state.wavedust === undefined) {
            state.wavedust = 0;
            console.log('[SaveManager] Migration: Added wavedust currency');
        }
    }

    /**
     * Mark existing items as legacy (grandfathered) for the new slot-based stat system
     * Legacy items keep their existing stats including timeScale and time-of-day conditions
     * New items will be generated with slot-specific stats only
     */
    _migrateGrandfatherItems(state) {
        if (!state.inventory?.items) return;

        let migratedCount = 0;
        state.inventory.items.forEach(item => {
            if (item.generationVersion === undefined) {
                item.generationVersion = 1;
                item.isLegacy = true;
                migratedCount++;
            }
        });

        // Also update equipped items
        if (state.inventory.equippedSlots) {
            Object.keys(state.inventory.equippedSlots).forEach(slot => {
                const item = state.inventory.equippedSlots[slot];
                if (item && item.generationVersion === undefined) {
                    item.generationVersion = 1;
                    item.isLegacy = true;
                }
            });
        }

        if (migratedCount > 0) {
            console.log(`[SaveManager] Migration: Grandfathered ${migratedCount} legacy items`);
        }
    }

    /**
     * Add star ratings to items that don't have them
     * Legacy items get default 2 stars (middle rating) since we can't
     * accurately recalculate their original roll quality
     */
    _migrateAddItemStars(state) {
        if (!state.inventory?.items) return;

        let migratedCount = 0;
        state.inventory.items.forEach(item => {
            if (item.stars === undefined) {
                // Default to 2 stars (middle) for items without a rating
                // Legacy items especially can't be accurately rated retroactively
                item.stars = 2;
                migratedCount++;
            }
        });

        // Also update equipped items
        if (state.inventory.equippedSlots) {
            Object.keys(state.inventory.equippedSlots).forEach(slot => {
                const item = state.inventory.equippedSlots[slot];
                if (item && item.stars === undefined) {
                    item.stars = 2;
                }
            });
        }

        if (migratedCount > 0) {
            console.log(`[SaveManager] Migration: Added star ratings to ${migratedCount} items`);
        }
    }

    /**
     * Add prestige fields to items that don't have them
     * Ensures backwards compatibility for the item prestige system
     * Migrates old single prestigeBonusStat to new array format
     */
    _migrateAddItemPrestige(state) {
        if (!state.inventory?.items) return;

        let migratedCount = 0;
        const migrateItem = (item) => {
            let migrated = false;

            if (item.prestigeLevel === undefined) {
                item.prestigeLevel = 0;
                migrated = true;
            }

            // Migrate old single bonus stat to array format
            if (item.prestigeBonusStats === undefined) {
                if (item.prestigeBonusStat) {
                    // Convert old format to array
                    item.prestigeBonusStats = [item.prestigeBonusStat];
                    delete item.prestigeBonusStat;
                } else {
                    item.prestigeBonusStats = [];
                }
                migrated = true;
            }

            return migrated;
        };

        state.inventory.items.forEach(item => {
            if (migrateItem(item)) migratedCount++;
        });

        // Also update equipped items
        if (state.inventory.equippedSlots) {
            Object.keys(state.inventory.equippedSlots).forEach(slot => {
                const item = state.inventory.equippedSlots[slot];
                if (item) {
                    migrateItem(item);
                }
            });
        }

        if (migratedCount > 0) {
            console.log(`[SaveManager] Migration: Added prestige fields to ${migratedCount} items`);
        }
    }

    /**
     * Add slotType to items that only have slot property
     * slotType was added for UI display consistency
     */
    _migrateAddItemSlotType(state) {
        let migratedCount = 0;

        const migrateItem = (item) => {
            if (item && item.slotType === undefined && item.slot) {
                item.slotType = item.slot;
                return true;
            }
            return false;
        };

        // Migrate inventory items
        if (state.inventory?.items) {
            state.inventory.items.forEach(item => {
                if (migrateItem(item)) migratedCount++;
            });
        }

        // Migrate equipped items
        if (state.inventory?.equippedSlots) {
            Object.keys(state.inventory.equippedSlots).forEach(slot => {
                const item = state.inventory.equippedSlots[slot];
                if (migrateItem(item)) migratedCount++;
            });
        }

        // Migrate items stored in opened packs (waves.availablePacks[].storedItems)
        if (state.waves?.availablePacks) {
            state.waves.availablePacks.forEach(pack => {
                if (pack.storedItems) {
                    pack.storedItems.forEach(item => {
                        if (migrateItem(item)) migratedCount++;
                    });
                }
            });
        }

        if (migratedCount > 0) {
            console.log(`[SaveManager] Migration: Added slotType to ${migratedCount} items`);
        }
    }

    /**
     * Add collection state structure for legacy saves
     * Collection persists forever across all prestiges
     */
    _migrateAddCollection(state) {
        if (!state.collection) {
            state.collection = {
                found: [],              // Array of baseId strings found
                mastery: {},            // { baseId: timesFound } for mastery tracking
                claimedMilestones: []   // Array of threshold values already claimed
            };
            console.log('[SaveManager] Migration: Added collection structure');
        }

        // Ensure all required fields exist (handles partial saves)
        if (!state.collection.found) {
            state.collection.found = [];
        }
        if (!state.collection.mastery) {
            state.collection.mastery = {};
        }
        if (!state.collection.claimedMilestones) {
            state.collection.claimedMilestones = [];
        }
    }
}
